import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger'
}: ConfirmDialogProps) {
  if (!isOpen) return null;
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };
  const variantStyles = {
    danger: {
      icon: 'text-red-400',
      iconBg: 'bg-red-500/20 border-red-500/30',
      button: 'bg-red-500 hover:bg-red-600'
    },
    warning: {
      icon: 'text-yellow-400',
      iconBg: 'bg-yellow-500/20 border-yellow-500/30',
      button: 'bg-yellow-500 hover:bg-yellow-600'
    },
    info: {
      icon: 'text-cyan-400',
      iconBg: 'bg-cyan-500/20 border-cyan-500/30',
      button: 'bg-cyan-500 hover:bg-cyan-600'
    }
  };
  const styles = variantStyles[variant];
  const dialogContent = <AnimatePresence data-id="element-196">
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-197">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={onClose} data-id="element-198" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-md" onClick={e => e.stopPropagation()} data-id="element-199">
          <GlassCard className="relative border-white/10 p-6" data-id="element-200">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors" data-id="element-201">
              <X size={16} data-id="element-202" />
            </button>

            <div className="text-center" data-id="element-203">
              <div className={`w-16 h-16 ${styles.iconBg} rounded-full flex items-center justify-center mx-auto mb-4 border`} data-id="element-204">
                <AlertTriangle size={32} className={styles.icon} data-id="element-205" />
              </div>

              <h2 className="text-2xl font-bold text-white mb-2" data-id="element-206">{title}</h2>
              <p className="text-gray-400 mb-6" data-id="element-207">{message}</p>

              <div className="flex gap-3" data-id="element-208">
                <button onClick={onClose} className="flex-1 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium transition-all" data-id="element-209">
                  {cancelText}
                </button>
                <button onClick={handleConfirm} className={`flex-1 px-4 py-3 rounded-xl ${styles.button} text-white font-bold transition-all`} data-id="element-210">
                  {confirmText}
                </button>
              </div>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </AnimatePresence>;
  return createPortal(dialogContent, document.body);
}